import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, UserPlus } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { formatApiError, teamAdd, teamList, teamRemove } from "../../lib/api";
import { inputCls } from "../../components/admin/fields";
import { formatDate } from "../../lib/format";

const EMPTY = { name: "", email: "", password: "" };

export default function AdminTeam() {
  const [members, setMembers] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);

  useEffect(() => { teamList().then(setMembers).catch((e) => toast.error(formatApiError(e))); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onAdd = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const m = await teamAdd(form);
      setMembers((list) => [...list, m]);
      setForm(EMPTY);
      toast.success(`${m.email} can now view analytics`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const confirmRemove = async () => {
    try {
      await teamRemove(pending.id);
      setMembers((list) => list.filter((m) => m.id !== pending.id));
      toast.success(`Removed ${pending.email}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setPending(null);
  };

  return (
    <div className="max-w-4xl space-y-8" data-testid="admin-team-page">
      <header>
        <p className="eyebrow mb-2">Access</p>
        <h1 className="font-playfair text-4xl text-neutral-900 tracking-tight">Team</h1>
        <p className="text-sm text-neutral-600 mt-2 max-w-2xl">
          Team members can sign in at /admin/login and view the Analytics dashboard only. They can't edit content or manage the team.
        </p>
      </header>

      <section className="bg-surface border border-neutral-200 rounded-xl overflow-hidden">
        <ul className="divide-y divide-neutral-200" data-testid="team-list">
          {(members || []).map((m) => (
            <li key={m.id} className="flex items-center gap-4 px-6 py-4" data-testid={`team-row-${m.email}`}>
              <div className="w-9 h-9 rounded-full bg-indigo-50 text-indigo-800 flex items-center justify-center font-playfair shrink-0">
                {(m.name || m.email).charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-neutral-900 truncate">{m.name || m.email}</p>
                <p className="text-xs text-neutral-500 truncate"><span className="sm:hidden font-semibold text-neutral-700">{m.role === "admin" ? "Owner · " : "Viewer · "}</span>{m.email}{m.created_at && ` · added ${formatDate(m.created_at)}`}</p>
              </div>
              <span className={`hidden sm:inline text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full ${m.role === "admin" ? "bg-indigo-800 text-surface" : "bg-neutral-100 text-neutral-700"}`}>
                {m.role === "admin" ? "Owner" : "Analytics viewer"}
              </span>
              {m.role !== "admin" && (
                <button type="button" onClick={() => setPending(m)} aria-label={`Remove ${m.email}`} className="p-2 rounded-md text-neutral-500 hover:text-red-700 hover:bg-red-50 transition-colors" data-testid={`team-remove-${m.email}`}>
                  <Trash2 size={15} />
                </button>
              )}
            </li>
          ))}
        </ul>
        {!members && <p className="px-6 py-6 text-sm text-neutral-500">Loading…</p>}
      </section>

      <form onSubmit={onAdd} className="bg-surface border border-neutral-200 rounded-xl p-6 sm:p-8" data-testid="team-add-form">
        <h2 className="font-playfair text-2xl text-neutral-900 mb-1">Add a team member</h2>
        <p className="text-sm text-neutral-600 mb-6">Share the temporary password privately; they can change it in Settings after signing in.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <input required placeholder="Name" value={form.name} onChange={set("name")} className={inputCls} data-testid="team-name-input" />
          <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={inputCls} data-testid="team-email-input" />
          <input required type="text" autoComplete="new-password" placeholder="Temporary password" value={form.password} onChange={set("password")} className={inputCls} data-testid="team-password-input" />
        </div>
        <p className="text-xs text-neutral-500 mt-2">At least 8 characters, with a letter and a number.</p>
        <div className="flex justify-end mt-5">
          <button type="submit" disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-indigo-800 text-surface text-sm font-semibold hover:bg-indigo-900 disabled:opacity-60 transition-colors" data-testid="team-add-button">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />} Give access
          </button>
        </div>
      </form>

      <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent data-testid="team-remove-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-playfair text-2xl">Remove access?</AlertDialogTitle>
            <AlertDialogDescription>{pending?.email} will be signed out and won't be able to view analytics.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="team-remove-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemove} className="bg-red-700 hover:bg-red-800" data-testid="team-remove-confirm">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
